import { toPlainText } from '@portabletext/react';

import type { CourseCardProps } from '@/components/page/courses/CourseCard';
import type { DataChipsProps } from '@/components/page/courses/DataChips/DataChips';
import type { IconAndTitleListProps } from '@/components/page/courses/IconAndTitleList/IconAndTitleList';
import type { NestedAccordionProps } from '@/components/page/courses/nestedAccordion/NestedAccordion';
import type { ProgrammeHeroProps } from '@/components/page/courses/ProgrammeHero/ProgrammeHero';
import type { SidebarProps } from '@/components/page/courses/Sidebar';
import type {
  SanityProgrammeB2BType,
  SanityProgrammeB2CType,
} from '@/types/sanity';
import type { WistiaPopover } from '@/types/shared';

interface ProgrammeSchemaProps {
  url: string;
  datePublished: string;
  programmeName:
    | SanityProgrammeB2BType['name']
    | SanityProgrammeB2CType['name'];
  description: ProgrammeHeroProps['subheading'];
  price?: SidebarProps['footer']['price'];
  wistiaPopover: WistiaPopover;
  aboutBadges: DataChipsProps['badges'];
  headline: ProgrammeHeroProps['heading'];
  programmeType: 'programmeB2B' | 'programmeB2C';
  learningOverviewList: IconAndTitleListProps['list'];
  syllabusSections: NestedAccordionProps['outerAccordion'];
  qualificationLevel: CourseCardProps['qualification']['level'];
}
export const ProgrammeSchema = ({
  url,
  datePublished,
  programmeName,
  description,
  wistiaPopover,
  headline,
  aboutBadges,
  programmeType,
  learningOverviewList,
  syllabusSections,
  qualificationLevel,
  price,
}: ProgrammeSchemaProps) => {
  const audience =
    programmeType === 'programmeB2B' ? 'Employers' : 'Individuals';

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Course',
          '@id': url,
          name: programmeName,
          description: description && toPlainText(description),
          datePublished,
          about: aboutBadges?.map((badge) => badge.tagText),
          inLanguage: 'en',
          headline: headline && toPlainText(headline),
          video: {
            '@type': 'VideoObject',
            name: wistiaPopover?.videoTitle,
            contentUrl: `https://fast.wistia.com/embed/medias/${wistiaPopover?.videoId}.jsonp`,
          },
          audience,
          teaches: teaches(learningOverviewList),
          syllabusSections: syllabusSectionsFormed(syllabusSections),
          educationalLevel:
            qualificationLevel && toPlainText(qualificationLevel),
          offers: price && [offer(price)],
        }),
      }}
    />
  );
};

const offer = (price: ProgrammeSchemaProps['price']) => {
  return price?.toLowerCase() === 'free'
    ? {
        '@type': 'Offer',
        category: 'Free',
      }
    : {
        '@type': 'Offer',
        category: 'Paid',
        priceCurrency: 'EUR',
        price: price,
      };
};

const teaches = (
  learningOverviewList: ProgrammeSchemaProps['learningOverviewList'],
) => {
  return learningOverviewList?.map(
    (item) => item.heading && toPlainText(item.heading),
  );
};

const syllabusSectionsFormed = (
  syllabusSections: ProgrammeSchemaProps['syllabusSections'],
) => {
  return syllabusSections?.flatMap((outerAccordion) =>
    outerAccordion?.accordion?.map((outerAccordionItem) => {
      const innerAccordionItemSyllabus = outerAccordionItem?.accordion?.map(
        (innerAccordionItem) => {
          return {
            '@type': 'Syllabus',
            name: innerAccordionItem?.heading,
            description:
              innerAccordionItem?.body && toPlainText(innerAccordionItem?.body),
          };
        },
      );

      const outerAccordionItemSyllabus = {
        '@type': 'Syllabus',
        name: outerAccordionItem?.heading,
        description:
          outerAccordionItem?.body && toPlainText(outerAccordionItem?.body),
      };
      return [outerAccordionItemSyllabus, ...innerAccordionItemSyllabus];
    }),
  );
};
